import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { TranslatePipe } from '../../../../../shared/pipes/translate.pipe';

@Component({
  selector: 'app-calculated-field',
  imports: [FormlyModule, TranslatePipe],
  templateUrl: './calculated-field.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './calculated-field.component.css',
})
export class CalculatedFieldComponent extends FieldType<FieldTypeConfig> {
  get formattedValue(): string | null {
    const value = this.formControl.value;
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value === 'number') {
      const calculationKind = (this.props['calculationKind'] as string | undefined) ?? '';
      const isDateDifference =
        calculationKind === 'date_diff_between_fields' ||
        calculationKind === 'date_diff_from_previous';

      if (isDateDifference) {
        return this.formatDuration(value, this.props['durationFormat'] as string | undefined);
      }

      return this.formatNumber(value, this.props['numberFormat'] as string | undefined);
    }

    return String(value);
  }

  private formatDuration(totalSeconds: number, format?: string): string {
    const sign = totalSeconds < 0 ? '-' : '';
    const remainingStart = Math.abs(Math.trunc(totalSeconds));

    const days = Math.floor(remainingStart / 86400);
    let remaining = remainingStart % 86400;
    const hours = Math.floor(remaining / 3600);
    remaining %= 3600;
    const minutes = Math.floor(remaining / 60);
    const seconds = remaining % 60;

    const parts: string[] = [];
    if (days) parts.push(`${days}d`);
    if (hours) parts.push(`${hours}h`);
    if (minutes) parts.push(`${minutes}m`);
    if (seconds || parts.length === 0) parts.push(`${seconds}s`);

    if (typeof format === 'string' && format.trim() !== '') {
      const formatted = format
        .replaceAll('{d}', String(days))
        .replaceAll('{h}', String(hours))
        .replaceAll('{m}', String(minutes))
        .replaceAll('{s}', String(seconds))
        .replaceAll('{total_h}', String(Math.trunc(remainingStart / 3600)))
        .replaceAll('{total_m}', String(Math.trunc(remainingStart / 60)))
        .replaceAll('{total_s}', String(remainingStart));
      return `${sign}${formatted}`;
    }

    return `${sign}${parts.join(' ')}`;
  }

  private formatNumber(value: number, format?: string): string {
    const formatSpec = (format ?? '').trim().toLowerCase();

    if (formatSpec === '' || formatSpec === 'default') {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(value);
    }

    const parts = formatSpec.split(':');
    const kind = parts[0];

    if (kind === 'integer') {
      return new Intl.NumberFormat('en-US', {
        maximumFractionDigits: 0,
      }).format(Math.trunc(value));
    }

    if (kind === 'decimal') {
      const digits = Number(parts[1]);
      const safeDigits = Number.isFinite(digits) ? Math.max(0, Math.min(10, digits)) : 2;
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: safeDigits,
        maximumFractionDigits: safeDigits,
      }).format(value);
    }

    if (kind === 'currency') {
      const code = (parts[1] || 'USD').toUpperCase();
      const digits = Number(parts[2]);
      const safeDigits = Number.isFinite(digits) ? Math.max(0, Math.min(10, digits)) : 2;
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: code,
        minimumFractionDigits: safeDigits,
        maximumFractionDigits: safeDigits,
      }).format(value);
    }

    if (kind === 'percent') {
      const digits = Number(parts[1]);
      const safeDigits = Number.isFinite(digits) ? Math.max(0, Math.min(10, digits)) : 0;
      return new Intl.NumberFormat('en-US', {
        style: 'percent',
        minimumFractionDigits: safeDigits,
        maximumFractionDigits: safeDigits,
      }).format(value);
    }

    if (kind === 'compact') {
      return new Intl.NumberFormat('en-US', {
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(value);
    }

    return String(value);
  }
}
